import { isNil } from 'lodash';
import DateTime from '~domain/model/core/date/datetime';
import { LanguageId } from '~domain/model/core/supported-languages';
import { decodeAccessToken } from '~frontend-common/helper/authentication/decode-access-token';
import { enqueueClarityAction } from '~frontend-common/helper/core/clarity';
import { passwordlessClient } from '~web-portal/clients/passwordless-client';
import { getUserAuth } from '~web-portal/helpers/get-user-auth';
import { AnalyticsEvent, analyticsService } from '~web-portal/services/analytics-service';
import { useAuthStore } from '~web-portal/stores';

class AuthService {
  public readonly OTP_REQUEST_COOLDOWN_SECONDS = 3 * 60;

  /**
   * Request a login OTP code
   */
  async requestOneTimePasscode(email: string, language: LanguageId): Promise<string> {
    enqueueClarityAction((clarity) => clarity('identify', email, null, null, email));

    const { otp } = useAuthStore.getState();
    if (!isNil(otp) && otp.email === email) {
      const generationDate = new DateTime(otp.generationDate);
      if (
        generationDate.isDateValid() &&
        // Add buffer of 15 seconds to account for the fact that the timer in the UI might have a slight difference.
        new DateTime().diffSecondsFromDate(generationDate) < this.OTP_REQUEST_COOLDOWN_SECONDS - 15
      ) {
        return otp.token;
      }
    }

    const response = await passwordlessClient.initiate({ email, language, type: 'otp', purpose: 'userAccountLogin' });

    const { setOtp } = useAuthStore.getState();
    setOtp({
      token: response.token,
      generationDate: new DateTime().formatToISO8601(),
      email,
    });

    return response.token;
  }

  /**
   * Logs in a user by getting an user id and access token
   */
  async login(passcodeToken: string, passcode: string): Promise<{ userId: string }> {
    const response = await passwordlessClient.validate({
      type: 'otp',
      otp: passcode,
      token: passcodeToken,
    });

    const { accessToken } = response;

    const payload = decodeAccessToken(accessToken);
    if (payload.resource.type !== 'userAccount') {
      throw new Error(`A userAccount token is required to authenticate. Got a ${payload.resource.type} token instead.`);
    }

    const userId = payload.resource.id;
    analyticsService.pushDataLayerEvent(AnalyticsEvent.Login, { userId });

    const { setAuth, clearOtp } = useAuthStore.getState();
    setAuth(userId, accessToken);
    clearOtp();

    return { userId };
  }

  /**
   * Request an email modification OTP code
   */
  async requestAccountModificationPasscode(newEmail: string, language: LanguageId): Promise<string> {
    const { accessToken } = getUserAuth();

    const response = await passwordlessClient.initiate(
      { newEmail, language, type: 'otp', purpose: 'userAccountEditSensitiveInfo' },
      accessToken,
    );
    return response.token;
  }

  /**
   * Validate email motification OTP code and return an access token
   */
  async validateEmailModificationPasscode(passcodeToken: string, passcode: string): Promise<string> {
    const response = await passwordlessClient.validate({
      type: 'otp',
      otp: passcode,
      token: passcodeToken,
    });

    return response.accessToken;
  }
}

export const authService = new AuthService();
